import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { useAuthStore } from './authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface SubscriptionPlan {
  id: string;
  name: string;
  display_name: string;
  description: string;
  monthly_price: number;
  yearly_price: number;
  price_per_user_monthly: number;
  price_per_user_yearly: number;
  min_users: number;
  max_users: number | null;
  boards_per_month: number | null;
  team_members_limit: number | null;
  features: string[];
  is_active: boolean;
}

interface TeamMemberLimit {
  current: number;
  limit: number | null;
  can_add: boolean;
  error_message: string | null;
}

interface Subscription {
  id: string;
  organization: {
    id: string;
    name: string;
    slug: string;
  } | null;
  plan: SubscriptionPlan;
  status: 'active' | 'cancelled' | 'expired' | 'trial';
  billing_cycle: 'monthly' | 'yearly';
  user_count: number;
  total_price: number;
  start_date: string;
  end_date: string | null;
  cancelled_at: string | null;
  is_active: boolean;
  boards_created_this_month: number;
  can_create_board: {
    can_create: boolean;
    error_message: string | null;
  };
  can_add_organization_member: {
    can_add: boolean;
    error_message: string | null;
  };
  team_member_limits: Record<string, TeamMemberLimit>;
  subscription_type: 'organization' | 'user';
  trial_start_date: string | null;
  trial_end_date: string | null;
  is_trial_active: boolean;
  is_trial_expired: boolean;
  trial_days_remaining: number;
}

interface BillingHistory {
  id: string;
  amount: number;
  billing_cycle: string;
  payment_date: string;
  status: string;
}

export interface PaddleCheckoutData {
  transaction_id: string;
  client_token: string;
  environment: 'production' | 'sandbox';
}

interface SubscriptionState {
  plans: SubscriptionPlan[];
  currentSubscription: Subscription | null;
  billingHistory: BillingHistory[];
  loading: boolean;
  error: string | null;
  fetchPlans: () => Promise<void>;
  fetchCurrentSubscription: (organizationId?: string) => Promise<void>;
  fetchBillingHistory: () => Promise<void>;
  upgradeSubscription: (planId: string, billingCycle: 'monthly' | 'yearly', organizationId?: string) => Promise<void>;
  cancelSubscription: (organizationId?: string) => Promise<void>;
  fetchTeamSubscription: (organizationId: string) => Promise<any>;
  createPaddleCheckout: (planId: string, billingCycle: 'monthly' | 'yearly', organizationId?: string) => Promise<PaddleCheckoutData>;
  confirmPaddleCheckout: (transactionId: string, organizationId?: string, billingCycle?: 'monthly' | 'yearly', planId?: string) => Promise<void>;
}

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      plans: [],
      currentSubscription: null,
      billingHistory: [],
      loading: false,
      error: null,

      fetchPlans: async () => {
        set({ loading: true, error: null });
        try {
          const response = await axios.get(`${API_BASE_URL}/subscription/plans/`);
          const plans = response.data.results || response.data;
          set({ plans: Array.isArray(plans) ? plans : [], loading: false });
        } catch (error) {
          console.error('Failed to fetch plans:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch plans',
            loading: false,
          });
        }
      },

      fetchCurrentSubscription: async (organizationId?: string) => {
        set({ loading: true, error: null });
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          const params = organizationId ? { organization_id: organizationId } : {};
          const response = await axios.get(
            `${API_BASE_URL}/subscription/subscriptions/current/`,
            { headers: { Authorization: `Bearer ${tokens.access}` }, params }
          );
          const subscriptionData = response.data;
          if (!subscriptionData.team_member_limits) {
            subscriptionData.team_member_limits = {};
          }
          set({ currentSubscription: subscriptionData, loading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch subscription',
            loading: false,
          });
        }
      },

      fetchBillingHistory: async () => {
        set({ loading: true, error: null });
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          const response = await axios.get(
            `${API_BASE_URL}/subscription/billing-history/`,
            { headers: { Authorization: `Bearer ${tokens.access}` } }
          );
          const billingHistory = response.data.results || response.data;
          set({ billingHistory: Array.isArray(billingHistory) ? billingHistory : [], loading: false });
        } catch (error) {
          console.error('Failed to fetch billing history:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch billing history',
            loading: false,
          });
        }
      },

      createPaddleCheckout: async (planId: string, billingCycle: 'monthly' | 'yearly', organizationId?: string) => {
        const tokens = useAuthStore.getState().tokens;
        if (!tokens?.access) {
          throw new Error('Not authenticated');
        }

        const response = await axios.post(
          `${API_BASE_URL}/subscription/subscriptions/create_checkout/`,
          { plan_id: planId, billing_cycle: billingCycle, organization_id: organizationId },
          { headers: { Authorization: `Bearer ${tokens.access}` } }
        );
        return response.data as PaddleCheckoutData;
      },

      confirmPaddleCheckout: async (transactionId: string, organizationId?: string, billingCycle?: 'monthly' | 'yearly', planId?: string) => {
        set({ loading: true, error: null });
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          const response = await axios.post(
            `${API_BASE_URL}/subscription/subscriptions/confirm_checkout/`,
            { transaction_id: transactionId, organization_id: organizationId, billing_cycle: billingCycle, plan_id: planId },
            { headers: { Authorization: `Bearer ${tokens.access}` } }
          );
          set({ currentSubscription: response.data, loading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to confirm checkout',
            loading: false,
          });
          throw error;
        }
      },

      upgradeSubscription: async (planId: string, billingCycle: 'monthly' | 'yearly', organizationId?: string) => {
        set({ loading: true, error: null });
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          const response = await axios.post(
            `${API_BASE_URL}/subscription/subscriptions/upgrade/`,
            { plan_id: planId, billing_cycle: billingCycle, organization_id: organizationId },
            { headers: { Authorization: `Bearer ${tokens.access}` } }
          );
          set({ currentSubscription: response.data, loading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to upgrade subscription',
            loading: false,
          });
          throw error;
        }
      },

      cancelSubscription: async (organizationId?: string) => {
        set({ loading: true, error: null });
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          const response = await axios.post(
            `${API_BASE_URL}/subscription/subscriptions/cancel/`,
            { organization_id: organizationId },
            { headers: { Authorization: `Bearer ${tokens.access}` } }
          );
          set({ currentSubscription: response.data, loading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to cancel subscription',
            loading: false,
          });
          throw error;
        }
      },

      fetchTeamSubscription: async (organizationId: string) => {
        set({ loading: true, error: null });
        try {
          const tokens = useAuthStore.getState().tokens;
          if (!tokens?.access) {
            throw new Error('Not authenticated');
          }

          const response = await axios.get(
            `${API_BASE_URL}/subscription/subscriptions/${organizationId}/team_subscription/`,
            { headers: { Authorization: `Bearer ${tokens.access}` } }
          );
          return response.data;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch team subscription',
            loading: false,
          });
          throw error;
        }
      },
    }),
    {
      name: 'subscription-storage',
      partialize: (state) => ({
        plans: state.plans,
      }),
    }
  )
);
